/** One life story — title, long-form body, and photographs. Used on the
 *  family memorial page (with edit/delete controls) and the public memorial. */
import { motion } from "framer-motion";
import { Edit3, Trash2, X } from "lucide-react";
import MultilingualText from "@/components/ui/MultilingualText";
import { fileUrl } from "@/lib/files";

export default function StoryCard({ story, canEdit = false, onEdit, onDelete }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="border border-[color:var(--sepia)]/25 bg-[color:var(--parchment)] p-6 md:p-10"
      data-testid={`story-${story.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="overline text-[color:var(--sepia)]">
          {story.author_name} ·{" "}
          {story.created_at &&
            new Date(story.created_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}
        </div>
        {canEdit && (
          <div className="flex gap-1 shrink-0">
            <button
              data-testid={`story-edit-${story.id}`}
              onClick={onEdit}
              className="p-2 border border-[color:var(--sepia)]/40 hover:bg-[color:var(--parchment-2)]"
              title="Edit story"
            >
              <Edit3 size={13} />
            </button>
            <button
              data-testid={`story-delete-${story.id}`}
              onClick={onDelete}
              className="p-2 border border-[color:var(--sepia)]/40 hover:bg-[color:var(--rust)] hover:text-[color:var(--bone)]"
              title="Delete story"
            >
              <Trash2 size={13} />
            </button>
          </div>
        )}
      </div>

      <MultilingualText
        value={story.title}
        className="mt-3 heritage-title text-3xl md:text-4xl leading-tight"
        testid={`story-title-${story.id}`}
      />

      {(story.photos || []).length > 0 && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3" data-testid={`story-photos-${story.id}`}>
          {story.photos.map((p, i) => (
            <div key={i} className="vintage-frame">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" style={{ filter: "sepia(15%)" }} />
              </div>
            </div>
          ))}
        </div>
      )}

      <MultilingualText
        value={story.body}
        className="mt-6 text-[17px] leading-[1.75] text-[color:var(--ink)] whitespace-pre-wrap max-w-[68ch]"
        testid={`story-body-${story.id}`}
      />
    </motion.article>
  );
}
